import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import DashboardSidebar from "../components/DashboardSidebar";
import DashboardHeader from "../components/DashboardHeader";
import {
  Lightbulb,
  Mic,
  ArrowRight,
  ArrowLeft,
  LayoutGrid,
  BookOpen,
  Loader2,
  Clock,
  Check,
  RefreshCw,
} from "lucide-react";
import { H2, SectionBody, sectionCard, useSections } from "../components/ArticleBlocks";
import { setCompletion, clearCompletion, isCompleted } from "../lib/progress";
import useStudyTimer from "../hooks/useStudyTimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InterviewTip = () => {
  useStudyTimer();
  const { tipSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tipsList, setTipsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const progressKey = `medily.progress.tips.${tipSlug}`;
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    setCompleted(isCompleted(progressKey));
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/dashboard`);
        const server = (data?.completed_tips || []).some((c) => c.slug === tipSlug);
        if (server) setCompleted(true);
      } catch (_) { /* fallback to local */ }
    })();
  }, [progressKey, tipSlug]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const [t, l] = await Promise.all([
          axios.get(`${API}/tips/${tipSlug}`),
          axios.get(`${API}/tips`),
        ]);
        if (!mounted) return;
        setData(t.data);
        setTipsList(l.data.tips || []);
      } catch (e) {
        setError("Tip not found.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [tipSlug]);

  const markComplete = useCallback(async () => {
    setCompletion(progressKey, {
      type: "tip",
      title: data?.topic?.title || tipSlug,
      href: `/interview-tips/${tipSlug}`,
    });
    setCompleted(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      await axios.post(`${API}/me/tip-complete`, { guide: "tips", slug: tipSlug });
    } catch (_) { /* noop */ }
  }, [progressKey, tipSlug, data]);

  const markIncomplete = useCallback(async () => {
    clearCompletion(progressKey);
    setCompleted(false);
    try {
      // Same endpoint pattern; DELETE not implemented for tips — leaving optimistic local clear.
    } catch (_) { /* noop */ }
  }, [progressKey]);

  const topic = data?.topic;
  const sections = useSections(topic?.blocks);

  const idx = tipsList.findIndex((t) => t.slug === tipSlug);
  const prev = idx > 0 ? tipsList[idx - 1] : null;
  const next = idx >= 0 && idx < tipsList.length - 1 ? tipsList[idx + 1] : null;

  return (
    <div className="min-h-screen bg-[#f5f8fd] flex">
      <DashboardSidebar />
      <div className="flex-1 min-w-0">
        <DashboardHeader />
        <main className="p-6 lg:p-10">
          {loading && (
            <div className="flex items-center justify-center py-24 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading tip…
            </div>
          )}
          {!loading && error && <div className="text-center text-red-500 py-24">{error}</div>}
          {!loading && !error && topic && (
            <>
              <div className="flex items-center gap-2 text-[13px] text-[#1d4ed8]">
                <LayoutGrid className="w-3.5 h-3.5" />
                <Link to="/dashboard/interviews" className="hover:underline">Interview Tips</Link>
                <span className="text-slate-400">&gt;</span>
                <span className="text-slate-500">{topic.title}</span>
              </div>

              <div className="mt-4 grid lg:grid-cols-[1fr_320px] gap-6 items-start">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1d4ed8] text-white flex items-center justify-center shrink-0 mt-1">
                    <Lightbulb className="w-6 h-6" />
                  </div>
                  <div>
                    <h1 className="text-[36px] md:text-[42px] leading-tight font-semibold text-slate-900 tracking-tight">{topic.title}</h1>
                    <div className="mt-3 flex items-center gap-3 text-[12.5px] text-slate-500 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-slate-200">
                        <Clock className="w-3 h-3" /> {topic.read_minutes} min read
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-slate-200">
                        <BookOpen className="w-3 h-3" /> Interview Tips
                      </span>
                      {completed && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#e6f7ee] text-[#15803d] border border-[#c1e9d0] font-semibold">
                          <Check className="w-3 h-3" /> Completed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="font-medium text-slate-800">Your Progress</span>
                    <span className={`font-semibold ${completed ? "text-[#15803d]" : "text-[#1d4ed8]"}`}>
                      {completed ? "100% Complete" : "0% Complete"}
                    </span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${completed ? "bg-[#15803d]" : "bg-[#1d4ed8]"}`}
                      style={{ width: completed ? "100%" : "0%" }} />
                  </div>
                  <p className="mt-3 text-[12px] text-slate-400">
                    {completed ? "You have completed this tip." : "Read the tip and mark it complete at the bottom."}
                  </p>
                </div>
              </div>

              <div className="mt-8 space-y-6">
                {sections.map((s, i) => (
                  <section key={i} className={`rounded-2xl border p-6 md:p-8 shadow-sm ${sectionCard(s.title)}`}>
                    {s.title && <div className="mb-4"><H2>{s.title}</H2></div>}
                    <SectionBody blocks={s.blocks} sectionTitle={s.title} />
                  </section>
                ))}
              </div>

              <section className="mt-8 rounded-2xl bg-gradient-to-r from-[#eef4ff] to-[#f6f9ff] border border-white p-6 md:p-8 flex items-center justify-between gap-4 flex-wrap shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#1d4ed8] text-white flex items-center justify-center">
                    <Mic className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1d4ed8] text-[16px]">Practise with Maya</p>
                    <p className="text-[13px] text-slate-600">
                      Try {topic.title.toLowerCase()} live in a realistic MMI station with Maya.
                    </p>
                  </div>
                </div>
                <button className="px-5 py-2.5 rounded-full bg-[#1d4ed8] text-white text-sm font-medium hover:bg-[#1e40af] inline-flex items-center gap-2 shadow-sm">
                  Practise Now <ArrowRight className="w-4 h-4" />
                </button>
              </section>

              <section className="mt-6 rounded-2xl bg-white border border-slate-100 shadow-sm p-6 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-semibold text-slate-900">
                    {completed ? "Nice work — you’ve completed this tip." : "Finished reading?"}
                  </p>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    {completed ? "Your progress is saved. You can revisit this tip anytime."
                      : "Mark it complete to track your progress across Interview Tips."}
                  </p>
                </div>
                {completed ? (
                  <button onClick={markIncomplete}
                    className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:border-[#1d4ed8] hover:text-[#1d4ed8] inline-flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" /> Mark as Incomplete
                  </button>
                ) : (
                  <button onClick={markComplete}
                    className="px-5 py-2.5 rounded-full bg-[#15803d] text-white text-sm font-medium hover:bg-[#166534] inline-flex items-center gap-2 shadow-sm">
                    <Check className="w-4 h-4" /> Mark as Complete
                  </button>
                )}
              </section>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                <button onClick={() => prev && navigate(`/interview-tips/${prev.slug}`)}
                  disabled={!prev}
                  className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between hover:border-[#1d4ed8] transition disabled:opacity-50 disabled:cursor-not-allowed text-left">
                  <div className="flex items-center gap-3">
                    <ArrowLeft className="w-4 h-4 text-slate-500" />
                    <div>
                      <p className="text-[12px] text-slate-500">Previous Tip</p>
                      <p className="font-medium text-slate-900 line-clamp-1">{prev ? prev.title : "—"}</p>
                    </div>
                  </div>
                </button>
                <button onClick={() => navigate("/dashboard/interviews")}
                  className="rounded-2xl border border-slate-200 bg-white p-4 inline-flex items-center justify-center gap-2 hover:border-[#1d4ed8] transition">
                  <LayoutGrid className="w-4 h-4 text-slate-500" />
                  <span className="font-medium text-slate-900">Back to Interview Tips</span>
                </button>
                <button onClick={() => next && navigate(`/interview-tips/${next.slug}`)}
                  disabled={!next}
                  className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between hover:border-[#1d4ed8] transition disabled:opacity-50 disabled:cursor-not-allowed text-right">
                  <div className="w-full">
                    <p className="text-[12px] text-slate-500">Next Tip</p>
                    <p className="font-medium text-slate-900 line-clamp-1">{next ? next.title : "—"}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-500 ml-3" />
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default InterviewTip;
